import type {
  ClarifyingAnswer,
  ClarifyingQuestion,
  ClarifyingQuestions,
} from '../core/types';

export function isQuestionAnswered(
  answer: ClarifyingAnswer | undefined
): boolean {
  if (answer === undefined) {
    return false;
  }
  return (
    answer.kind === 'skipped' ||
    (answer.kind === 'option' && (answer.optionIds?.length ?? 0) > 0) ||
    (answer.kind === 'other' && (answer.text?.trim().length ?? 0) > 0)
  );
}

/** Count of questions with a non-empty answer. Questions are never required —
 * this feeds progress copy ("1 of 2 answered"), not an approval gate. */
export function countAnsweredQuestions(
  questions: ClarifyingQuestion[],
  answersById: Record<string, ClarifyingAnswer>
): number {
  return questions.filter(q => isQuestionAnswered(answersById[q.id])).length;
}

export const ACTIONS_LOCKED_MESSAGE = 'Answer the question above to continue';
// Shown while the answered gating question is being applied (refine turn running).
export const ACTIONS_REFINING_MESSAGE = 'Applying your answers…';
export type ActionsLockReason = 'unanswered' | 'refining';

export function hasGatingQuestion(questions: ClarifyingQuestion[]): boolean {
  return questions.some(q => q.gatesActions === true);
}

// Actions are locked while a gating question is unanswered, and stay locked
// while the submitted answers are being applied (the clarifying refine turn
// running). Returns null when not locked.
//
// `isClarifyingRefineInFlight` MUST be true only for the refine turn kicked off
// by submitting these answers — NOT for any later ordinary chat turn. Passing a
// generic "a chat turn is processing" boolean here mislabels a normal turn as
// "Applying your answers…" and wrongly re-locks approval (the host tracks the
// clarifying refine specifically and passes that).
//
// The `unanswered` branch reads `answers_submitted_at` off the proposal
// envelope; the host clears that stamp optimistically the moment a submitted
// gating answer is edited, so an in-flight (still-debouncing) autosave cannot
// leave approval momentarily unlocked against stale server state.
export function getActionsLockReason(
  clarifyingQuestions: ClarifyingQuestions | null | undefined,
  isClarifyingRefineInFlight: boolean
): ActionsLockReason | null {
  const questions = clarifyingQuestions?.questions ?? [];
  if (!hasGatingQuestion(questions)) {
    return null;
  }
  if (!clarifyingQuestions?.answers_submitted_at) {
    return 'unanswered';
  }
  return isClarifyingRefineInFlight ? 'refining' : null;
}

export function areActionsLockedByGating(
  clarifyingQuestions: ClarifyingQuestions | null | undefined,
  isClarifyingRefineInFlight: boolean
): boolean {
  return (
    getActionsLockReason(clarifyingQuestions, isClarifyingRefineInFlight) !==
    null
  );
}

export function actionsLockMessage(reason: ActionsLockReason): string {
  return reason === 'unanswered'
    ? ACTIONS_LOCKED_MESSAGE
    : ACTIONS_REFINING_MESSAGE;
}
